import { DatePicker } from '@progress/kendo-react-dateinputs';
import { FieldRenderProps, FieldWrapper } from '@progress/kendo-react-form';
import { Input } from '@progress/kendo-react-inputs';
import { FloatingLabel, Label } from '@progress/kendo-react-labels';
import { InputFieldDatePickerMaskedBox } from 'components/shared/form/grid/filter_cells';
import moment from 'moment';
import React from 'react';
// @ts-ignore
import styles from './form.module.scss';

interface CustomDateInputProps extends FieldRenderProps {
  disabled: boolean,
  id: any,
  label: string,
  // eslint-disable-next-line react/require-default-props
  className?: any
}

export const CustomDateInput = (props: CustomDateInputProps) => {
  const {
    label, id, value, valid, disabled, onChange, defaultValue
  } = props;
  const [focused, setFocused] = React.useState(undefined);
  const onFocusDatePickerInput = () => {
    setFocused(true);
  };
  const onBlurDatePickerInput = () => {
    setFocused(false);
  };
  return (
    <FieldWrapper className={props.className}>
      { /* @ts-ignore */}
      <FloatingLabel
        label={label}
        editorId={id}
        editorValue={value || defaultValue}
        editorValid={valid}
        editorDisabled={disabled}
      >
        <Input
          id={id}
          value={value || ''}
          disabled={disabled}
          valid={valid}
          readOnly={props.readOnly}
          type={focused || props.value || defaultValue ? 'date' : 'text'}
          placeholder={focused ? 'mm/dd/yyyy' : null}
          aria-label={focused ? 'mm/dd/yyyy' : 'mm/dd/yyyy'}
          onChange={onChange}
          onFocus={onFocusDatePickerInput}
          onBlur={onBlurDatePickerInput}
          max={props.max ?? '9999-12-31'}
          defaultValue={defaultValue}
          className={`${props.readOnly ? styles.readOnly : ''}`}
        />
      </FloatingLabel>
    </FieldWrapper>
  );
};

interface CustomDateInputUnwrappedProps extends FieldRenderProps {
  disabled: boolean,
  id: any,
  label: string,
}

export const CustomDateInputUnwrapped = (props: CustomDateInputUnwrappedProps) => {
  const {
    label, id, value, valid, disabled, onChange, defaultValue
  } = props;
  const [focused, setFocused] = React.useState(undefined);
  const onFocusDatePickerInput = () => {
    setFocused(true);
  };
  const onBlurDatePickerInput = () => {
    setFocused(false);
  };
  return (
    <>
      { /* @ts-ignore */}
      <FloatingLabel
        label={label}
        editorId={id}
        editorValue={value || defaultValue}
        editorValid={valid}
        editorDisabled={disabled}
      >
        <Input
          id={id}
          value={value || ''}
          disabled={disabled}
          valid={valid}
          readOnly={props.readOnly}
          type={focused || props.value || defaultValue ? 'date' : 'text'}
          placeholder={focused ? 'mm/dd/yyyy' : null}
          aria-label={focused ? 'mm/dd/yyyy' : 'mm/dd/yyyy'}
          onChange={onChange}
          onFocus={onFocusDatePickerInput}
          onBlur={onBlurDatePickerInput}
          max={props.max ?? '9999-12-31'}
          defaultValue={defaultValue}
        />
      </FloatingLabel>
    </>
  );
};

export const CustomKendoDatePicker = (props) => {
  const {
    label, value, disabled, defaultValue, id, min, max, readOnly, name, className, valid
  } = props;

  const onChange = (event) => {
    if (event.value === undefined) {
      return;
    }
    const momentObj = moment(event.value);

    // New version of DatePickers accept null as empty value only
    if (momentObj.isValid()) {
      props.onChange({
        value: momentObj.isValid() ? momentObj.format('MM/DD/yyyy') : '',
        target: {
          value: momentObj.isValid() ? momentObj.format('MM/DD/yyyy') : '',
          name
        }
      });
    } else {
      props.onChange({
        value: null,
        target: {
          value: null,
          name
        }
      });
    }
  };

  const handlePaste = (event) => {
    const pastedValue = event.clipboardData.getData('text').trim();
    const parsedDate = moment(pastedValue, 'MM/DD/yyyy');

    if (parsedDate.isValid()) {
      onChange({
        value: pastedValue,
        operator: 'eq',
        syntheticEvent: event.syntheticEvent,
      });
    }
  };

  const onFocus = (event) => {
    event.target.addEventListener('paste', handlePaste);
  };

  const onBlur = (event) => {
    event.target.removeEventListener('paste', handlePaste);
  };

  return (
    <div className={styles.datePicker}>
      <Label editorId={id}>{label}</Label>
      <DatePicker
        id={id}
        name={name}
        aria-label={props.ariaLabel}
        dateInput={InputFieldDatePickerMaskedBox}
        formatPlaceholder={{ year: 'yyyy', month: 'mm', day: 'dd' }}
        defaultValue={defaultValue ? new Date(defaultValue) : undefined}
        max={max ? new Date(max) : undefined}
        min={min ? new Date(min) : undefined}
        disabled={disabled || readOnly}
        value={value && value !== '' ? new Date(moment(value).format('MM/DD/yyyy')) : null}
        onChange={(event) => onChange(event)}
        onFocus={(event) => onFocus(event)}
        onBlur={(event) => onBlur(event)}
        className={`${(disabled || readOnly) ? styles.readOnly : className}`}
        valid={valid}
      />
    </div>
  );
};
